import React, { useEffect, useState } from "react";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  GridFilterInputValueProps,
  GridFilterOperator,
  GridRenderCellParams,
} from "@mui/x-data-grid-premium";
import dvdlocator from "../../../../apis/dvdlocator";
import { renderDate } from "../../../../formatting/dateFormat";
import {
  DriftCell,
  InventoryGridLink,
  ProvenanceCell,
  ProvenanceDialog,
  ProvenanceFrame,
  inventoryGridUrlForInventoryIds,
  money,
  useProvenanceDetail,
} from "./MetricProvenance";

/**
 * MAVCG: the product's master average cost of goods, rolled up into one cell
 * like MRCOG.
 *   AVG   all-time: the cheapest complete combination where each part costs
 *         the lowest average cost (inventory.avcg) among its inventory rows
 *   90d   the same over the last 90 days of purchases (AVCG-H 90)
 * Clicking opens the detail: the winning combination, the inventory row that
 * priced each part with its cost then and now, a link to those rows in the
 * Inventory grid and a "Recalculate" action. No freshness dot: the costs
 * come from our own purchase history, not supplier data.
 */

export const MAVCG_KIND_LABELS = {
  AVG: "AVG: all-time average cost of goods",
  AVG90: "90d: average cost of goods over the last 90 days",
} as const;

export type MavcgKind = keyof typeof MAVCG_KIND_LABELS;

export const MAVCG_DESCRIPTION =
  "Master average cost of goods: the sum of the average cost of the in-stock components needed to fulfill this product, for the cheapest complete-set combination. AVG = all-time average cost; 90d = average over the last 90 days of purchases (AVCG-H 90). Click for the inventory rows that produced each value.";

export interface MavcgRowValues {
  avg: number | null;
  avg90: number | null;
  avgUpdatedAt: string | number | null;
  avg90UpdatedAt: string | number | null;
  avgCombination: number | null;
  avg90Combination: number | null;
}

export function getMavcgValues(row: any): MavcgRowValues {
  const p = row?.product ?? {};
  return {
    avg: p.master_avg_cost_of_goods ?? null,
    avg90: p.master_avg_cogs_ninety_day ?? null,
    avgUpdatedAt: p.mavcg_updated_at ?? null,
    avg90UpdatedAt: p.mavcg_90_updated_at ?? null,
    avgCombination: p.mavcg_combination_id ?? null,
    avg90Combination: p.mavcg_90_combination_id ?? null,
  };
}

export function formatMavcgTooltip(v: MavcgRowValues): string {
  const line = (
    kind: MavcgKind,
    value: number | null,
    updated: string | number | null
  ) =>
    value === null
      ? `${MAVCG_KIND_LABELS[kind]}: none`
      : `${MAVCG_KIND_LABELS[kind]}: ${money(value)} (calculated ${
          updated ? renderDate(updated) : "never"
        })`;
  return [
    line("AVG", v.avg, v.avgUpdatedAt),
    line("AVG90", v.avg90, v.avg90UpdatedAt),
    "Click for the inventory rows behind each value.",
  ].join("\n");
}

export const renderMavcgCell = (params: GridRenderCellParams) => {
  const v = getMavcgValues(params.row);
  if (v.avg === null && v.avg90 === null) return null;
  return (
    <ProvenanceCell
      tooltip={formatMavcgTooltip(v)}
      renderDetail={(close) => (
        <MavcgDetail
          productId={params.row.product?.product_id}
          productTitle={params.row.product?.product_title}
          onClose={close}
        />
      )}
    >
      <span>
        <span style={{ color: "rgba(0,0,0,0.55)", fontSize: "0.8em" }}>
          AVG{" "}
        </span>
        {money(v.avg)}
      </span>
      <span>
        <span style={{ color: "rgba(0,0,0,0.55)", fontSize: "0.8em" }}>
          90d{" "}
        </span>
        {money(v.avg90)}
      </span>
    </ProvenanceCell>
  );
};

// ─── Detail ──────────────────────────────────────────────────────────────────

export interface CogsPart {
  part_number: string;
  inventory_id: number | null;
  inventory_title: string | null;
  cost_then: number | null;
  cost_now: number | null;
}

export interface CogsKindDetail {
  cost: number | null;
  updated_at: string | null;
  combination_id: number | null;
  parts: CogsPart[];
}

export interface CogsDetailData {
  product_id: number;
  AVG: CogsKindDetail;
  AVG90: CogsKindDetail;
}

export async function fetchCogsDetail(
  productId: number
): Promise<CogsDetailData> {
  const response = await dvdlocator.get(`/product/${productId}/metrics/cogs`);
  return response.data;
}

function KindSection({
  kind,
  data,
}: {
  kind: MavcgKind;
  data: CogsKindDetail;
}) {
  const ids = data.parts
    .map((p) => p.inventory_id)
    .filter((id): id is number => id !== null);
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2">
        {MAVCG_KIND_LABELS[kind]}: {money(data.cost)}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {data.cost === null
          ? "No complete combination could be costed."
          : `Combination #${data.combination_id ?? "?"}, calculated ${
              data.updated_at ? renderDate(data.updated_at) : "never"
            }. "Now" shows the row's current cost when it changed since.`}
      </Typography>
      {data.parts.length > 0 && (
        <Table size="small" sx={{ mt: 0.5 }}>
          <TableHead>
            <TableRow>
              <TableCell>Part</TableCell>
              <TableCell>Inventory row</TableCell>
              <TableCell align="right">Cost then</TableCell>
              <TableCell align="right">Now</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.parts.map((part) => (
              <TableRow key={`${kind}-${part.part_number}`}>
                <TableCell>{part.part_number}</TableCell>
                <TableCell title={part.inventory_title ?? undefined}>
                  {part.inventory_id === null ? "—" : `#${part.inventory_id}`}
                  {part.inventory_title
                    ? ` ${part.inventory_title.slice(0, 40)}${
                        part.inventory_title.length > 40 ? "…" : ""
                      }`
                    : ""}
                </TableCell>
                <TableCell align="right">{money(part.cost_then)}</TableCell>
                <TableCell align="right">
                  <DriftCell
                    then={part.cost_then}
                    now={part.cost_now}
                    format={money}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <InventoryGridLink
        href={inventoryGridUrlForInventoryIds(ids)}
        count={ids.length}
        noun="inventory row"
      />
    </Box>
  );
}

export function MavcgDetail({
  productId,
  productTitle,
  onClose,
}: {
  productId: number;
  productTitle?: string;
  onClose?: () => void;
}) {
  const { detail, error } = useProvenanceDetail(
    productId,
    fetchCogsDetail,
    "average cost detail"
  );
  return (
    <ProvenanceFrame
      title="Average cost of goods (MAVCG)"
      productTitle={productTitle}
      productId={productId}
      error={error}
      loaded={detail !== null}
      recalcLabel="Recalculate MAVCG"
      onClose={onClose}
    >
      {detail && (
        <>
          <KindSection kind="AVG" data={detail.AVG} />
          <KindSection kind="AVG90" data={detail.AVG90} />
        </>
      )}
    </ProvenanceFrame>
  );
}

export function MavcgDetailDialog({
  open,
  onClose,
  product,
}: {
  open: boolean;
  onClose: () => void;
  product: any;
}) {
  return (
    <ProvenanceDialog
      open={open}
      onClose={onClose}
      title="Average cost of goods (MAVCG)"
    >
      <MavcgDetail
        productId={product?.product_id}
        productTitle={product?.product_title}
      />
    </ProvenanceDialog>
  );
}

// ─── Filtering ────────────────────────────────────────────────────────────────

export interface MavcgFilterValue {
  avg?: { operator: string; value?: string | number };
  avg90?: { operator: string; value?: string | number };
  avgUpdatedAt?: {
    operator: "within" | "olderThan" | "never" | "ever";
    days?: number;
  };
}

const NUMBER_OPERATORS = [
  { value: ">", label: ">" },
  { value: ">=", label: "≥" },
  { value: "<", label: "<" },
  { value: "<=", label: "≤" },
  { value: "=", label: "=" },
  { value: "!=", label: "≠" },
  { value: "isEmpty", label: "is empty" },
  { value: "isNotEmpty", label: "is not empty" },
];
const VALUELESS = new Set(["isEmpty", "isNotEmpty"]);

export function isMavcgFilterComplete(value: unknown): boolean {
  if (!value || typeof value !== "object") return false;
  const v = value as MavcgFilterValue;
  const numberOk = (c?: { operator: string; value?: unknown }) =>
    !!c?.operator &&
    (VALUELESS.has(c.operator) || (c.value !== undefined && c.value !== ""));
  const dateOk = (c?: { operator: string; days?: number }) =>
    !!c?.operator &&
    (c.operator === "never" ||
      c.operator === "ever" ||
      Number.isFinite(Number(c.days)));
  return numberOk(v.avg) || numberOk(v.avg90) || dateOk(v.avgUpdatedAt);
}

function NumberCondition({
  label,
  condition,
  onChange,
}: {
  label: string;
  condition?: { operator: string; value?: string | number };
  onChange: (c?: { operator: string; value?: string | number }) => void;
}) {
  const operator = condition?.operator ?? "";
  return (
    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
      <FormControl size="small" sx={{ minWidth: 150 }}>
        <InputLabel>{label}</InputLabel>
        <Select
          label={label}
          value={operator}
          onChange={(e) => {
            const op = e.target.value;
            onChange(
              op ? { operator: op, value: condition?.value } : undefined
            );
          }}
        >
          <MenuItem value="">any</MenuItem>
          {NUMBER_OPERATORS.map((op) => (
            <MenuItem key={op.value} value={op.value}>
              {op.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {operator && !VALUELESS.has(operator) && (
        <TextField
          size="small"
          type="number"
          value={condition?.value ?? ""}
          onChange={(e) =>
            onChange({
              operator,
              value: e.target.value === "" ? undefined : e.target.value,
            })
          }
          sx={{ width: 110 }}
        />
      )}
    </Box>
  );
}

function DateCondition({
  label,
  condition,
  onChange,
}: {
  label: string;
  condition?: MavcgFilterValue["avgUpdatedAt"];
  onChange: (c?: MavcgFilterValue["avgUpdatedAt"]) => void;
}) {
  const op = condition?.operator ?? "";
  return (
    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
      <FormControl size="small" sx={{ minWidth: 150 }}>
        <InputLabel>{label}</InputLabel>
        <Select
          label={label}
          value={op}
          onChange={(e) => {
            const next = e.target.value as typeof op;
            onChange(
              next
                ? { operator: next as any, days: condition?.days ?? 7 }
                : undefined
            );
          }}
        >
          <MenuItem value="">any time</MenuItem>
          <MenuItem value="within">within last</MenuItem>
          <MenuItem value="olderThan">older than</MenuItem>
          <MenuItem value="ever">calculated</MenuItem>
          <MenuItem value="never">never calculated</MenuItem>
        </Select>
      </FormControl>
      {(op === "within" || op === "olderThan") && (
        <TextField
          size="small"
          type="number"
          label="days"
          value={condition?.days ?? ""}
          onChange={(e) =>
            onChange({
              operator: op,
              days: e.target.value === "" ? undefined : Number(e.target.value),
            })
          }
          slotProps={{ htmlInput: { min: 0 } }}
          sx={{ width: 90 }}
        />
      )}
    </Box>
  );
}

export interface MavcgFilterFieldsProps {
  value: MavcgFilterValue | undefined;
  onChange: (value: MavcgFilterValue) => void;
}

export function MavcgFilterFields({ value, onChange }: MavcgFilterFieldsProps) {
  const v: MavcgFilterValue = value ?? {};
  const set = (patch: Partial<MavcgFilterValue>) =>
    onChange({ ...v, ...patch });
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
        py: 0.5,
        minWidth: 280,
      }}
    >
      <NumberCondition
        label="MAVCG (all-time)"
        condition={v.avg}
        onChange={(c) => set({ avg: c })}
      />
      <NumberCondition
        label="MAVCG-H 90"
        condition={v.avg90}
        onChange={(c) => set({ avg90: c })}
      />
      <DateCondition
        label="Calculated"
        condition={v.avgUpdatedAt}
        onChange={(c) => set({ avgUpdatedAt: c })}
      />
      <Typography variant="caption" color="text.secondary">
        AVG = all-time average cost of goods; 90d = over the last 90 days of
        purchases.
      </Typography>
    </Box>
  );
}

function MavcgFilterInput({ item, applyValue }: GridFilterInputValueProps) {
  const [draft, setDraft] = useState<MavcgFilterValue>(item.value ?? {});
  useEffect(() => {
    setDraft(item.value ?? {});
  }, [item.value]);
  return (
    <MavcgFilterFields
      value={draft}
      onChange={(next) => {
        setDraft(next);
        applyValue({ ...item, value: next });
      }}
    />
  );
}

export function getMavcgFilterOperators(): GridFilterOperator[] {
  return [
    {
      label: "matches",
      value: "rollup",
      getApplyFilterFn: () => null,
      InputComponent: MavcgFilterInput,
    },
  ];
}
